import { CSSProperties, RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { CARET } from 'enums';

import { TRIGGER_PADDING } from '../../../constants';

interface Position {
  top: CSSProperties['top'];
  left: CSSProperties['left'];
  transform: CSSProperties['transform'];
  caret: CARET;
}

interface HoverTooltip {
  is_open: boolean;
  caret: CARET;
  triggerRef: RefObject<HTMLDivElement>;
  tooltipRef: RefObject<HTMLDivElement>;
  positionRef: RefObject<Position>;
}

export const useHoverTooltip = (): HoverTooltip => {
  const [is_open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<Position>({ top: 0, left: 0, transform: '50%', caret: CARET.BOTTOM });
  const observerRef = useRef<ResizeObserver | null>(null);
  const [caret, setCaret] = useState<CARET>(CARET.BOTTOM);

  const calculatePosition = useCallback(
    (triggerRect: DOMRect, tooltipRect: { width: number; height: number }) => {
      const vertical_center = triggerRect.top + triggerRect.height / 2;
      const horizontal_center = triggerRect.left + triggerRect.width / 2;
      const POSITION_THRESHOLD = 5;

      // right placement
      if (
        triggerRect.right + TRIGGER_PADDING + tooltipRect.width <=
        window.innerWidth - TRIGGER_PADDING - POSITION_THRESHOLD
      ) {
        return {
          top: vertical_center,
          left: triggerRect.right + TRIGGER_PADDING,
          transform: 'translate(0, -50%)',
          caret: CARET.LEFT,
        };
      }

      // left placement
      if (
        triggerRect.left - tooltipRect.width - TRIGGER_PADDING >=
        TRIGGER_PADDING + POSITION_THRESHOLD
      ) {
        return {
          top: vertical_center,
          left: triggerRect.left - tooltipRect.width - TRIGGER_PADDING,
          transform: 'translate(0, -50%)',
          caret: CARET.RIGHT,
        };
      }

      // bottom placement
      if (
        triggerRect.bottom + TRIGGER_PADDING + tooltipRect.height <=
        window.innerHeight - TRIGGER_PADDING - POSITION_THRESHOLD
      ) {
        return {
          top: triggerRect.bottom + TRIGGER_PADDING,
          left: horizontal_center,
          transform: 'translate(-50%, 0)',
          caret: CARET.TOP,
        };
      }

      // top placement (fallback)
      return {
        top: triggerRect.top - tooltipRect.height - TRIGGER_PADDING,
        left: horizontal_center,
        transform: 'translate(-50%, 0)',
        caret: CARET.BOTTOM,
      };
    },
    []
  );

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger || !tooltip) return;

    const triggerRect = trigger.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();

    positionRef.current = calculatePosition(triggerRect, tooltipRect);

    tooltip.style.top = `${positionRef.current.top}px`;
    tooltip.style.left = `${positionRef.current.left}px`;
    tooltip.style.transform = positionRef.current.transform || '';
    tooltip.style.opacity = '1';

    setCaret(positionRef.current.caret);
  }, [calculatePosition]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    requestAnimationFrame(() => requestAnimationFrame(updatePosition));
  }, [updatePosition]);

  const handleClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    observerRef.current = new ResizeObserver(updatePosition);
    observerRef.current.observe(trigger);

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      observerRef.current?.disconnect();
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;
    if (!trigger) return;

    trigger.addEventListener('mouseenter', handleOpen);
    trigger.addEventListener('mouseleave', handleClose);

    if (tooltip) {
      tooltip.addEventListener('mouseenter', handleOpen);
      tooltip.addEventListener('mouseleave', handleClose);
    }

    return () => {
      trigger.removeEventListener('mouseenter', handleOpen);
      trigger.removeEventListener('mouseleave', handleClose);
      if (tooltip) {
        tooltip.removeEventListener('mouseenter', handleOpen);
        tooltip.removeEventListener('mouseleave', handleClose);
      }
    };
  }, [handleOpen, handleClose]);

  return { is_open, caret, triggerRef, tooltipRef, positionRef };
};
